import { useState } from "react";
import { CalendarPlus, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, formatINRCompact } from "@/lib/utils";
import type { CalendarEventItem } from "@/types/task";
import { dateLocale, withClock } from "@/lib/dateTime";

const DOT: Record<string, string> = { meeting: "bg-gold", call: "bg-info", external: "bg-info", whatsapp: "bg-success", deal: "bg-success", done: "bg-muted-foreground", task: "bg-primary" };

function kindOf(e: CalendarEventItem) {
  if (e.kind === "EXTERNAL") return "external";
  if (e.kind === "DEAL") return "deal";
  if (e.task_status === "DONE") return "done";
  if (e.task_type === "MEETING") return "meeting";
  if (e.task_type === "CALL") return "call";
  if (e.task_type === "WHATSAPP") return "whatsapp";
  return "task";
}
/** Tasks can be moved; calendar events from Google or Outlook and deal close dates cannot */
const movable = (e: CalendarEventItem) => e.kind === "TASK" && e.task_status !== "DONE";
const clock = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function dayHeading(day: Date) {
  const diff = Math.round((day.getTime() - startOfDay(new Date()).getTime()) / 86400000);
  const date = day.toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short" });
  return diff === 0 ? `Today · ${date}` : diff === 1 ? `Tomorrow · ${date}` : diff === -1 ? `Yesterday · ${date}` : date;
}

/**
 * Everything in the range as one list in date order, grouped by day. Days with nothing are left out.
 * Uses the same items, layers, clash marks and click behaviour as the grid views.
 */
export function AgendaList({
  events,
  clashIds,
  selectedId,
  showOwner,
  loading,
  rangeStart,
  rangeDays,
  onOpen,
  onNew,
  onMove,
}: {
  events: CalendarEventItem[];
  clashIds: Set<string>;
  selectedId?: string;
  showOwner?: boolean;
  loading?: boolean;
  rangeStart: Date;
  rangeDays: number;
  onOpen: (e: CalendarEventItem) => void;
  onNew: () => void;
  /** Move a task to another day, keeping its time of day */
  onMove: (e: CalendarEventItem, day: Date) => void;
}) {
  const [dragging, setDragging] = useState<CalendarEventItem | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const sorted = [...events].sort((a, b) => {
    // All-day first within a day, then by start time
    const d = startOfDay(new Date(a.start)).getTime() - startOfDay(new Date(b.start)).getTime();
    return d || Number(!!b.all_day) - Number(!!a.all_day) || new Date(a.start).getTime() - new Date(b.start).getTime();
  });
  const days: { key: number; day: Date; items: CalendarEventItem[] }[] = [];
  for (const e of sorted) {
    const day = startOfDay(new Date(e.start));
    let slot = days.find((d) => d.key === day.getTime());
    if (!slot) (slot = { key: day.getTime(), day, items: [] }), days.push(slot);
    slot.items.push(e);
  }

  // While something is being dragged every day of the range is a place to drop it, empty ones too
  if (dragging) {
    for (let i = 0; i < rangeDays; i++) {
      const day = new Date(startOfDay(rangeStart).getFullYear(), startOfDay(rangeStart).getMonth(), startOfDay(rangeStart).getDate() + i);
      if (!days.some((d) => d.key === day.getTime())) days.push({ key: day.getTime(), day, items: [] });
    }
    days.sort((a, b) => a.key - b.key);
  }

  const dropOn = (day: Date) => {
    const moving = dragging;
    setDragging(null);
    setOver(null);
    if (moving && startOfDay(new Date(moving.start)).getTime() !== day.getTime()) onMove(moving, day);
  };
  const shift = (e: CalendarEventItem, by: number) => {
    const d = startOfDay(new Date(e.start));
    onMove(e, new Date(d.getFullYear(), d.getMonth(), d.getDate() + by));
  };

  if (!loading && days.length === 0)
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-card py-14 text-center">
        <CalendarPlus className="size-6 text-muted-foreground" />
        <span className="text-sm font-semibold">Nothing scheduled in this period</span>
        <span className="max-w-sm text-xs text-muted-foreground">Meetings, timed tasks, calendar events and deal close dates will be listed here.</span>
        <Button size="sm" onClick={onNew}>
          New meeting
        </Button>
      </div>
    );

  return (
    <div className="flex flex-col gap-3" aria-busy={loading}>
      <p className="text-[11.5px] text-muted-foreground">Drag a task to another day to move it, or focus it and press Shift + ← / →. Its time stays the same.</p>
      {days.map(({ key, day, items }) => (
        <section
          key={key}
          className="flex flex-col gap-1.5"
          onDragOver={(ev) => {
            if (!dragging) return;
            ev.preventDefault();
            setOver(key);
          }}
          onDrop={(ev) => {
            ev.preventDefault();
            dropOn(day);
          }}
        >
          <h3 className={cn("flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-wide", day.getTime() === startOfDay(new Date()).getTime() ? "text-primary" : "text-muted-foreground")}>
            {dayHeading(day)} <span className="font-mono text-[11px]">{items.length}</span>
          </h3>
          <div className={cn("overflow-hidden rounded-lg border bg-card", dragging && over === key ? "border-primary bg-accent/40 ring-2 ring-primary/30" : dragging ? "border-dashed border-border" : "border-border")}>
            {items.length === 0 && <div className="px-3.5 py-3 text-xs text-muted-foreground">Drop here to move it to this day</div>}
            {items.map((e, i) => {
              const kind = kindOf(e);
              const clash = clashIds.has(e.id);
              const owner = e.assigned_to ? `${e.assigned_to.first_name || ""} ${e.assigned_to.last_name || ""}`.trim() : "";
              const detail = e.kind === "DEAL" ? [e.amount ? formatINRCompact(e.amount) : "", e.description].filter(Boolean).join(" · ") : e.related_name || e.location || "";
              return (
                <button
                  key={e.id}
                  type="button"
                  draggable={movable(e)}
                  onDragStart={(ev) => {
                    if (!movable(e)) return;
                    ev.dataTransfer.effectAllowed = "move";
                    ev.dataTransfer.setData("text/plain", e.id);
                    setDragging(e);
                  }}
                  onDragEnd={() => (setDragging(null), setOver(null))}
                  onKeyDown={(ev) => {
                    // Shift + arrow moves a task by a day without the mouse
                    if (!movable(e) || !ev.shiftKey || (ev.key !== "ArrowRight" && ev.key !== "ArrowLeft")) return;
                    ev.preventDefault();
                    shift(e, ev.key === "ArrowRight" ? 1 : -1);
                  }}
                  onClick={() => onOpen(e)}
                  aria-description={movable(e) ? "Drag to another day, or press Shift and the left or right arrow to move it a day" : undefined}
                  className={cn("flex w-full flex-wrap items-center gap-3 px-3.5 py-2 text-left hover:bg-muted/50", i > 0 && "border-t border-border/70", selectedId === e.id && "bg-accent/50", dragging?.id === e.id && "opacity-50", movable(e) && "cursor-grab active:cursor-grabbing")}
                >
                  {movable(e) && <GripVertical className="-ml-1.5 size-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />}
                  <span className="w-[104px] shrink-0 font-mono text-xs text-muted-foreground">{e.all_day ? "All day" : `${clock(e.start)}–${clock(e.end)}`}</span>
                  <span className={cn("size-2 shrink-0 rounded-full", DOT[kind])} aria-hidden="true" />
                  <span className="flex min-w-[200px] flex-1 flex-col leading-snug">
                    <span className={cn("text-sm font-medium", kind === "done" && "text-muted-foreground line-through")}>{e.title}</span>
                    {(detail || (showOwner && owner)) && (
                      <span className="text-[11.5px] text-muted-foreground">
                        {detail}
                        {detail && showOwner && owner ? " · " : ""}
                        {showOwner ? owner : ""}
                      </span>
                    )}
                  </span>
                  {e.priority === "HIGH" && <span className="rounded bg-danger-soft px-1.5 text-[11px] font-semibold leading-5 text-danger">High</span>}
                  {clash && <span className="rounded bg-danger-soft px-1.5 text-[11px] font-semibold leading-5 text-danger">Clash</span>}
                  {e.kind === "EXTERNAL" && <span className="rounded bg-info-soft px-1.5 text-[11px] font-semibold leading-5 text-info">{e.provider === "GOOGLE" ? "Google" : "Outlook"}</span>}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
